import type {
  NoticeBlock,
  NoticeCatalog,
  NoticeComposition,
  NoticeContextField,
  NoticeRun,
} from './notice-fidelity.models';

/** A transient source locator, never a new persisted reference identity. */
export interface NoticeReferenceSelection {
  blockId: string;
  referenceIndex: number;
  kind: 'field' | 'term';
  code: string;
  format?: string;
}

export type NoticeReferenceCommand =
  | { type: 'binding'; code: string }
  | { type: 'format'; format: string | null };

export type NoticeReferenceResult =
  | {
      ok: true;
      composition: NoticeComposition;
      selection: NoticeReferenceSelection;
    }
  | { ok: false; message: string };

/** Ordinals count protected references only, independently of literal normalization. */
export function noticeReferenceSelections(
  block: NoticeBlock,
): NoticeReferenceSelection[] {
  return (block.runs ?? [])
    .filter((run) => run.kind === 'field' || run.kind === 'term')
    .map((run, referenceIndex) => ({
      blockId: block.id,
      referenceIndex,
      kind: run.kind as 'field' | 'term',
      code: run.value,
      ...(run.format === undefined ? {} : { format: run.format }),
    }));
}

/** Verify the ordinal and the expected identity before using a transient selection. */
export function noticeSelectedReference(
  document: NoticeComposition,
  selection: NoticeReferenceSelection | null,
): NoticeRun | undefined {
  if (
    !selection ||
    !Number.isInteger(selection.referenceIndex) ||
    selection.referenceIndex < 0 ||
    (selection.kind !== 'field' && selection.kind !== 'term')
  )
    return undefined;
  const matches = document.blocks.filter(
    (block) => block.id === selection.blockId,
  );
  if (matches.length !== 1 || !['paragraph', 'box'].includes(matches[0].kind))
    return undefined;
  const run = matches[0].runs?.filter(
    (candidate) => candidate.kind === 'field' || candidate.kind === 'term',
  )[selection.referenceIndex];
  return run &&
    run.kind === selection.kind &&
    run.value === selection.code &&
    run.format === selection.format
    ? run
    : undefined;
}

/** Only an exact retained profile can supply typed binding choices. */
export function noticeReferenceContextMatches(
  document: NoticeComposition,
  catalog: NoticeCatalog,
): boolean {
  const expected = catalog.documentContext;
  return !!(
    document.contractCode === 'notice-document-context-proof' &&
    document.version === 1 &&
    expected &&
    document.context?.code === expected.code &&
    document.context.version === expected.version &&
    document.context.hash === expected.hash
  );
}

export function noticeReferenceChoices(
  document: NoticeComposition,
  catalog: NoticeCatalog,
  kind: 'field' | 'term',
): readonly { code: string; label: string }[] {
  const entries =
    document.contractCode === 'notice-document-context-proof'
      ? noticeReferenceContextMatches(document, catalog)
        ? kind === 'field'
          ? catalog.documentContext!.fields
          : catalog.documentContext!.terms
        : []
      : document.contractCode === 'notice-fidelity-experimental' &&
          document.version === 1
        ? kind === 'field'
          ? catalog.fields
          : catalog.terms
        : [];
  // Ambiguous catalog identities must not silently choose an arbitrary entry.
  return entries.filter(
    (entry) =>
      !!entry.code &&
      entries.filter((candidate) => candidate.code === entry.code).length === 1,
  );
}

export function noticeReferenceField(
  document: NoticeComposition,
  catalog: NoticeCatalog,
  code: string,
): NoticeContextField | undefined {
  return noticeReferenceContextMatches(document, catalog) &&
    noticeReferenceChoices(document, catalog, 'field').some(
      (entry) => entry.code === code,
    )
    ? catalog.documentContext!.fields.find((entry) => entry.code === code)
    : undefined;
}

/** A bounded immutable change; the caller still owns source, history and authority. */
export function applyNoticeReferenceCommand(
  document: NoticeComposition,
  catalog: NoticeCatalog,
  selection: NoticeReferenceSelection,
  command: NoticeReferenceCommand,
): NoticeReferenceResult {
  if (
    document.version !== 1 ||
    !['notice-fidelity-experimental', 'notice-document-context-proof'].includes(
      document.contractCode,
    )
  )
    return fail('Reference edits require a supported retained v1 composition.');
  if (
    document.contractCode === 'notice-document-context-proof' &&
    !noticeReferenceContextMatches(document, catalog)
  )
    return fail(
      'The exact registered document context is unavailable. Refresh the contract before editing references.',
    );
  const current = noticeSelectedReference(document, selection);
  if (!current)
    return fail(
      'This reference selection changed. Select the reference again.',
    );

  const next = structuredClone(document);
  const run = noticeSelectedReference(next, selection)!;
  if (command.type === 'binding') {
    if (
      typeof command.code !== 'string' ||
      !noticeReferenceChoices(document, catalog, selection.kind).some(
        (entry) => entry.code === command.code,
      )
    )
      return fail(
        'Choose a registered reference from the same binding family.',
      );
    if (command.code !== run.value) {
      run.value = command.code;
      // Changing the binding deliberately adopts its registered default format.
      delete run.format;
    }
  } else if (command.type === 'format') {
    if (command.format === null) delete run.format;
    else {
      const field =
        selection.kind === 'field'
          ? noticeReferenceField(document, catalog, selection.code)
          : undefined;
      if (
        !field ||
        typeof command.format !== 'string' ||
        !field.formats.includes(command.format)
      )
        return fail('Choose a permitted format for the registered data field.');
      run.format = command.format;
    }
  } else return fail('Choose a supported reference edit.');

  const limit = Math.min(
    32768,
    Number.isInteger(catalog.limits.sourceBytes) &&
      catalog.limits.sourceBytes > 0
      ? catalog.limits.sourceBytes
      : 32768,
  );
  if (
    new TextEncoder().encode(JSON.stringify(next, null, 2)).byteLength > limit
  )
    return fail(
      'This edit exceeds the supported source byte limit; shorten or remove content first.',
    );
  return {
    ok: true,
    composition: next,
    selection: {
      blockId: selection.blockId,
      referenceIndex: selection.referenceIndex,
      kind: run.kind as 'field' | 'term',
      code: run.value,
      ...(run.format === undefined ? {} : { format: run.format }),
    },
  };
}

function fail(message: string): NoticeReferenceResult {
  return { ok: false, message };
}
